/* eslint-disable react-hooks/exhaustive-deps */
import { Dispatch, useEffect, useState } from "react";

import {
  Box,
  Button,
  Center,
  Circle,
  Heading,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text
} from "@chakra-ui/react";

import { format, subDays } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BiSolidCheckCircle } from "react-icons/bi";
import { MdOutlineCalendarMonth } from "react-icons/md";
import { TbBuildingBank, TbHistory, TbWallet } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IBank } from "@/jenesys-types/jenesys-entities/config";

import {
  fetchBanks,
  selectConnectionsSettings,
  specifyConnectionDuration
} from "@/store/features/clientSettings/connections/connections";

enum STAGES {
  CHOOSE_DURATION,
  SUCCESS
}

interface DateRange {
  name: string;
  from: Date;
  to: Date;
}

const today = new Date();

const dateRanges = [
  {
    name: "Last 30 days",
    from: subDays(today, 30),
    to: today
  },
  {
    name: "Last 60 days",
    from: subDays(today, 60),
    to: today
  },
  {
    name: "Last 90 days",
    from: subDays(today, 90),
    to: today
  },
  {
    name: "Custom Date",
    from: subDays(today, 1),
    to: today
  }
];

const last90days = subDays(today, 90);

const ChooseDuration = ({
  setDateRange,
  dateRange,
  isSavingDuration,
  handleSetChosenDuration
}: {
  setDateRange: Dispatch<React.SetStateAction<DateRange>>;
  dateRange: DateRange;
  isSavingDuration: boolean;
  handleSetChosenDuration: () => void;
}) => (
  <Box>
    <Heading fontWeight={700} fontSize="md">
      Almost Done
    </Heading>
    <Text mt={1} pb={2} fontSize={13} fontWeight={600}>
      Choose Data Fetch Duration
    </Text>
    <Text mt={4} fontSize={14} fontWeight="500">
      How far back would you like to pull your transaction data? Please select
      one of the options below:
    </Text>
    <Stack mt={4}>
      {dateRanges.map((range) => {
        const isSelected = dateRange.name === range.name;
        return (
          <HStack
            cursor="pointer"
            key={range.name}
            onClick={() => setDateRange(range)}
          >
            <Center
              border="2px solid"
              borderColor={isSelected ? "black" : "gray.400"}
              boxSize={4}
              rounded="full"
            >
              <Circle size={2} bg={isSelected ? "black" : "transparent"} />
            </Center>
            <Text ml={1} fontSize={14} fontWeight={500}>
              {range.name}
            </Text>
          </HStack>
        );
      })}
    </Stack>
    {dateRange.name === "Custom Date" && (
      <HStack spacing={4} mt={4}>
        <Box>
          <Text mb={1} fontSize={12} fontWeight="500">
            From
          </Text>

          <Box pos="relative">
            <Icon
              top={2}
              right={3}
              pos="absolute"
              color="gray.500"
              as={MdOutlineCalendarMonth}
            />

            <DatePicker
              selected={dateRange.from}
              dateFormat="dd/MM/yyyy"
              minDate={subDays(today, 90)}
              onChange={(date) =>
                setDateRange((prev) => ({ ...prev, from: date! }))
              }
            />
          </Box>
        </Box>

        <Box>
          <Text mb={1} fontSize={12} fontWeight="500">
            To
          </Text>

          <Box pos="relative">
            <Icon
              top={2}
              right={3}
              pos="absolute"
              color="gray.500"
              as={MdOutlineCalendarMonth}
            />

            <DatePicker
              selected={dateRange.to}
              dateFormat="dd/MM/yyyy"
              maxDate={today}
              onChange={(date) =>
                setDateRange((prev) => ({ ...prev, to: date! }))
              }
            />
          </Box>
        </Box>
      </HStack>
    )}
    <Text my={4} fontSize={14} fontWeight="500">
      Note: The maximum allowable duration is 90 days.
    </Text>

    <Button
      size="sm"
      isLoading={isSavingDuration}
      loadingText="Confirming..."
      onClick={handleSetChosenDuration}
      isDisabled={!dateRange}
      w="full"
      h={9}
    >
      Confirm Selection
    </Button>
  </Box>
);

const Success = ({
  bank,
  fromDate,
  handleViewConnection,
  onClose
}: {
  bank?: IBank;
  fromDate: Date;
  handleViewConnection: () => void;
  onClose: () => void;
}) => (
  <Box>
    <Icon fontSize={48} color="green.500" as={BiSolidCheckCircle} />
    <Text fontWeight={700}>Thank you</Text>
    <Text mt={2} fontSize={14}>
      We have received the requested information from your selected account(s)
    </Text>

    <HStack mt={8} spacing={3}>
      <Image boxSize={7} src={bank?.raw.media[0]?.source} alt={bank?.name} />

      <Text fontSize={14} fontWeight={700}>
        {bank?.name}
      </Text>
    </HStack>

    <Stack mt={4} spacing={2}>
      <HStack>
        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={TbBuildingBank} color="secondary.500" />
        </Center>
        <Text fontSize={14}>Your Bank Account(s) details</Text>
      </HStack>

      <HStack>
        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={TbWallet} color="secondary.500" />
        </Center>
        <Text fontSize={14}>Your Account(s) Balance Information</Text>
      </HStack>

      <HStack>
        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={TbHistory} color="secondary.500" />
        </Center>
        <Text fontSize={14}>Your Transaction history</Text>
      </HStack>
    </Stack>

    <Text mt={6} mb={4} fontSize={14}>
      We will access your information from your accounts from&nbsp;
      {format(fromDate, "dd/MM/yyyy")}.
    </Text>

    <HStack spacing={6}>
      <Button w="full" size="sm" h={9} onClick={onClose} variant="outline">
        Close
      </Button>
      <Button onClick={handleViewConnection} h={9} size="sm" w="full">
        View Connection
      </Button>
    </HStack>
  </Box>
);

export function ChooseFetchDuration({ clientId, disclosure }: any) {
  const { isOpen, onClose } = disclosure;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const connectionId = searchParams.get("connection_id")!;
  const [stage, setStage] = useState<STAGES>(STAGES.CHOOSE_DURATION);
  const [dateRange, setDateRange] = useState<DateRange>({
    name: "Last 90 days",
    from: last90days,
    to: today
  });

  const { isSavingDuration, banks, bankConnections } = useSelector(
    selectConnectionsSettings
  );

  useEffect(() => {
    if (!banks.length) dispatch(fetchBanks());
  }, [banks.length]);

  const connection = bankConnections.find(({ id }) => id === connectionId);
  const bank = banks.find(({ id }) => id === connection?.institution_id);

  const handleSetChosenDuration = () => {
    dispatch(
      specifyConnectionDuration({
        connectionId,
        fromDate: format(dateRange?.from, "yyyy-MM-dd"),
        toDate: format(dateRange?.to, "yyyy-MM-dd"),
        companyId: clientId,
        onSuccess: () => setStage(STAGES.SUCCESS)
      })
    );
  };

  const handleViewConnection = () => {
    navigate(`bank-reconciliation/yapily`);
  };

  return (
    <Modal
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent rounded={16}>
        <ModalBody fontWeight={500} px={6} pb={8} pt={12}>
          {stage === STAGES.CHOOSE_DURATION && (
            <ChooseDuration
              setDateRange={setDateRange}
              dateRange={dateRange}
              isSavingDuration={isSavingDuration}
              handleSetChosenDuration={handleSetChosenDuration}
            />
          )}

          {stage === STAGES.SUCCESS && (
            <Success
              bank={bank}
              fromDate={dateRange.from}
              handleViewConnection={handleViewConnection}
              onClose={onClose}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
